import { convertLength } from "@rothoblaas/design-system";
import { useWatch } from "react-hook-form";
import { ConcreteResinFormValues } from "../../../http/concreteResin/types/calculations";
import { UNIT_SYSTEM_IMPERIAL } from "../../../types";

// Imperial is display only: the form, the backend and the saved entries always hold mm. Millimeters
// are shown as whole numbers; inches as fractions down to the 64th, as US drill bits and tapes read them.

const roundTo = (value: number, decimals: number): number => {
  const factor = 10 ** decimals;
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

export const roundMillimeters = (millimeters: number): number => Math.round(millimeters);

// Keeps 3 decimals of mm, not whole mm, so that converting back gives exactly the inches typed
// (whole mm would turn a typed 4.5" into 4.488" under the cursor).
export const inchesToMillimeters = (inches: number): number => roundTo(convertLength(inches, "in", "mm"), 3);

// For components inside the resin calculation's FormProvider.
export const useIsImperial = (): boolean =>
  useWatch<ConcreteResinFormValues, "unitSystem">({ name: "unitSystem" }) === UNIT_SYSTEM_IMPERIAL;

// A whole number of 64ths of an inch as its reduced fraction: 26 -> 13/32, 72 -> 1 1/8.
const formatSixtyFourths = (sixtyFourths: number): string => {
  let numerator = sixtyFourths;
  let denominator = 64;
  while (numerator % 2 === 0 && denominator > 1) {
    numerator /= 2;
    denominator /= 2;
  }
  const whole = Math.floor(numerator / denominator);
  const rest = numerator % denominator;
  if (rest === 0) return `${whole}`;
  return whole === 0 ? `${rest}/${denominator}` : `${whole} ${rest}/${denominator}`;
};

// US drill bits are sized in fractions of an inch, down to 64ths: a hole drilled with one shows as
// its bit (13/32, 1 1/8). Null when the hole isn't a whole number of 64ths of an inch - a metric hole
// shown in inches.
export const toDrillBitFraction = (millimeters: number): string | null => {
  const inches = convertLength(millimeters, "mm", "in");
  const sixtyFourths = Math.round(inches * 64);
  if (sixtyFourths <= 0 || Math.abs(inches * 64 - sixtyFourths) > 0.01) return null;
  return formatSixtyFourths(sixtyFourths);
};

// Any length in inches, rounded to the nearest 64th, as a fraction (4 59/64), as the form shows it
// (MeasurementInput).
export const millimetersToInchFraction = (millimeters: number): string =>
  formatSixtyFourths(Math.round(convertLength(millimeters, "mm", "in") * 64));

// The US drill bit an imperial calculation drills a metric hole with: the smallest 1/64" size not
// smaller than it (10 mm -> 13/32"), as Rothoblaas' engineers define it, since no US bit drills the
// metric hole exactly. In inches; the epsilon keeps a hole that already is a 64th from rounding up to
// the next one.
export const toUsDrillBitInches = (metricHoleMillimeters: number): number =>
  Math.ceil(convertLength(metricHoleMillimeters, "mm", "in") * 64 - 1e-9) / 64;

// The same bit's exact mm, unrounded: an imperial calculation's hole, as the calculation stays in mm.
export const toUsDrillBitMillimeters = (metricHoleMillimeters: number): number =>
  convertLength(toUsDrillBitInches(metricHoleMillimeters), "in", "mm");
